import * as THREE from 'three';
const MAX_LIGHTS = 3;
const positions = [
    new THREE.Vector3(),
    new THREE.Vector3(),
    new THREE.Vector3(),
];
const colors = [new THREE.Color(), new THREE.Color(), new THREE.Color()];
// x = rayon, y = intensite. Intensite 0 = halo inactif.
const params = [
    new THREE.Vector2(1, 0),
    new THREE.Vector2(1, 0),
    new THREE.Vector2(1, 0),
];
// Un seul jeu d'uniformes partage par tous les materiaux patches : regler les
// lumieres une fois suffit, sans avoir a retrouver les materiaux un par un.
const shared = {
    uFatLightPos: { value: positions },
    uFatLightColor: { value: colors },
    uFatLightParams: { value: params },
};
const VERTEX_PARS = `
varying vec3 vFatWorld;
varying vec3 vFatNormal;
`;
const VERTEX_BODY = `
vFatWorld = (modelMatrix * vec4(transformed, 1.0)).xyz;
// Une geometrie sans attribut de normale recoit (0,0,0) de WebGL, et
// normalize() y renvoie NaN — le fragment devient noir ou transparent. D'ou
// le repli vers la verticale.
vec3 fatN = mat3(modelMatrix) * normal;
vFatNormal = dot(fatN, fatN) > 0.0 ? normalize(fatN) : vec3(0.0, 1.0, 0.0);
`;
const FRAGMENT_PARS = `
varying vec3 vFatWorld;
varying vec3 vFatNormal;
uniform vec3 uFatLightPos[${MAX_LIGHTS}];
uniform vec3 uFatLightColor[${MAX_LIGHTS}];
uniform vec2 uFatLightParams[${MAX_LIGHTS}];
`;
const FRAGMENT_BODY = `
for (int fatI = 0; fatI < ${MAX_LIGHTS}; fatI++) {
    vec3 toLight = uFatLightPos[fatI] - vFatWorld;
    float dist = length(toLight);
    float att = 1.0 - clamp(dist / uFatLightParams[fatI].x, 0.0, 1.0);
    att *= att;
    // Eclairage enveloppant plutot qu'un dot() franc : ces halos sont de
    // grandes nappes diffuses, un terme net y dessinerait une arete sur
    // chaque volume.
    float facing = 0.5 + 0.5 * dot(vFatNormal, toLight / max(dist, 0.0001));
    float amount = clamp(att * facing * uFatLightParams[fatI].y, 0.0, 1.0);
    outgoingLight = mix(outgoingLight, max(outgoingLight, uFatLightColor[fatI]), amount);
}
`;
/**
 * Branche les halos sur un materiau existant, sans le remplacer : les
 * materiaux bakes gardent leur texture et leur encodage, les materiaux
 * proceduraux gardent leurs couleurs de sommets.
 */
export const applySceneLighting = (material) => {
    const mat = material;
    if (mat.userData && mat.userData.fatLit)
        return material;
    mat.userData = mat.userData || {};
    mat.userData.fatLit = true;
    const previous = mat.onBeforeCompile;
    mat.onBeforeCompile = (shader, renderer) => {
        if (previous)
            previous(shader, renderer);
        shader.uniforms.uFatLightPos = shared.uFatLightPos;
        shader.uniforms.uFatLightColor = shared.uFatLightColor;
        shader.uniforms.uFatLightParams = shared.uFatLightParams;
        shader.vertexShader = shader.vertexShader
            .replace('#include <common>', '#include <common>' + VERTEX_PARS)
            .replace('#include <begin_vertex>', '#include <begin_vertex>' + VERTEX_BODY);
        shader.fragmentShader = shader.fragmentShader
            .replace('#include <common>', '#include <common>' + FRAGMENT_PARS)
            .replace('#include <output_fragment>', FRAGMENT_BODY + '#include <output_fragment>');
    };
    // Sans cle distincte, three reutilise le programme deja compile d'un
    // materiau non patche ayant les memes defines, et le patch n'a aucun
    // effet visible.
    mat.customProgramCacheKey = () => 'fat-lit';
    mat.needsUpdate = true;
    return material;
};
/** Pose les halos. Au-dela de trois, les suivants sont ignores. */
export const configureSceneLights = (lights) => {
    for (let i = 0; i < MAX_LIGHTS; i++) {
        const light = lights[i];
        if (!light) {
            params[i].set(1, 0);
            continue;
        }
        positions[i].copy(light.position);
        // Le rendu sort en sRGB et traite ces couleurs comme lineaires : sans
        // conversion, un bleu moyen ressortirait presque blanc.
        colors[i].set(light.color).convertSRGBToLinear();
        params[i].set(Math.max(1, light.radius), light.strength);
    }
};
